'use client'

import { useEffect, useRef, useCallback } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from '@/lib/auth-client'

// Views are buffered for everyone, but only ever sent for a signed-in member.
// The endpoint requires a session, so flushing for anonymous visitors only ever
// earned a 401 — roughly 98k a week, each one waking a route handler for
// nothing. (They did not cost a database round-trip: Better Auth's cookieCache
// is on, and an anonymous request carries no session cookie to validate.)
// Buffering before the session resolves keeps the first page view of a
// member's visit.
const MAX_BUFFERED_VIEWS = 50

export default function PageViewTracker() {
  const pathname = usePathname()
  const { data: session } = useSession()
  const lastTrackedPath = useRef<string | null>(null)
  const lastTrackedTime = useRef<number>(0)
  const bufferRef = useRef<Array<{ path: string; referrer?: string }>>([])
  const initialReferrer = useRef<string | null>(
    typeof document !== 'undefined' ? document.referrer || null : null,
  )

  // flushBuffer is referenced by a long-lived interval, so read auth state
  // through a ref rather than closing over it. The ref is synced in the effect
  // below, not during render.
  const isAuthenticated = Boolean(session?.user)
  const isAuthenticatedRef = useRef(isAuthenticated)

  const flushBuffer = useCallback(() => {
    if (!isAuthenticatedRef.current) return
    if (bufferRef.current.length === 0) return

    const views = bufferRef.current.splice(0)
    const body = JSON.stringify({ views })

    // Send batch via sendBeacon (fire-and-forget, doesn't block page unload).
    // NOTE: sendBeacon returns true if the request was queued, regardless of
    // server response. A 404 from an old server (no batch endpoint) still
    // counts as "sent" — that batch of views is lost. This is an acceptable
    // tradeoff during rolling deploy: analytics data loss for one 30s window
    // on old servers, no user-facing impact. Once all servers are updated,
    // every batch succeeds.
    if (typeof navigator.sendBeacon === 'function') {
      const sent = navigator.sendBeacon(
        '/api/analytics/pageview-batch',
        new Blob([body], { type: 'application/json' }),
      )
      if (!sent) {
        // sendBeacon itself failed (e.g., payload too large) — try fetch fallback
        for (const view of views) {
          fetch('/api/analytics/pageview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(view),
          }).catch(() => {})
        }
      }
    } else {
      // No sendBeacon support — individual POSTs
      for (const view of views) {
        fetch('/api/analytics/pageview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(view),
        }).catch(() => {})
      }
    }
  }, [])

  // Flush buffer on interval and when user leaves
  useEffect(() => {
    const interval = setInterval(flushBuffer, 30000)

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushBuffer()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      flushBuffer() // flush on unmount
    }
  }, [flushBuffer])

  // Sync auth state into the ref, then send anything that was buffered before
  // the session resolved.
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated
    if (isAuthenticated) flushBuffer()
  }, [isAuthenticated, flushBuffer])

  // Track page views with cooldown
  useEffect(() => {
    if (!pathname || pathname === lastTrackedPath.current) return

    // 5-second cooldown to prevent rapid navigation spam
    const now = Date.now()
    if (now - lastTrackedTime.current < 5000) return

    lastTrackedPath.current = pathname
    lastTrackedTime.current = now

    const view: { path: string; referrer?: string } = { path: pathname }

    // Only include referrer on the very first page view (external referrer)
    if (initialReferrer.current) {
      try {
        const ref = new URL(initialReferrer.current)
        if (ref.origin !== window.location.origin) {
          view.referrer = initialReferrer.current
        }
      } catch {
        // invalid referrer URL, skip
      }
      initialReferrer.current = null
    }

    // Anonymous visitors never flush, so cap the buffer rather than letting it
    // grow for the whole visit.
    if (bufferRef.current.length >= MAX_BUFFERED_VIEWS) {
      bufferRef.current.shift()
    }
    bufferRef.current.push(view)
  }, [pathname])

  return null
}
